import ExcelJS from 'exceljs';

const REPORT_VAT_RATES = new Map([
  // Polska — ceny A1/A2/A4 w kalkulatorach są brutto, raport pokazuje netto.
  ['a1-amberone-pl', 0.23],
  ['a2-awsa-pl', 0.23],
  ['a4-sam-pl', 0.23],

  // Chorwacja — ceny z kalkulatorów autostradowych są brutto z VAT 25%.
  // VAT odejmujemy PRZED przeliczeniem EUR -> PLN po kursie NBP.
  ['hac-hr', 0.25],
  ['azm-hr', 0.25],
  ['bina-istra-hr', 0.25],
]);

function reportVatRate(calculatorKey) {
  return REPORT_VAT_RATES.get(String(calculatorKey || '').trim()) ?? null;
}

function reportNetValue(calculatorKey, value) {
  const amount = finiteNumberOrNull(value);
  if (amount === null) return null;

  const vatRate = reportVatRate(calculatorKey);
  return vatRate === null
    ? amount
    : roundMoney(amount / (1 + vatRate));
}

function reportVatLabel(calculatorKey) {
  const vatRate = reportVatRate(calculatorKey);
  return vatRate === null
    ? 'Koszt'
    : `Koszt netto (VAT ${Math.round(vatRate * 100)}%)`;
}

function reportCostText(section, result) {
  if (!result) return '';
  const calculatorKey = String(result?.calculatorKey || section?.calculatorKey || '');
  const net = reportNetValue(calculatorKey, result?.costValue);
  if (reportVatRate(calculatorKey) === null || net === null) return result?.costText || '';
  const currency = normalizeCurrencyCode(result?.currency || parseCurrencyFromText(result?.costText)) || 'PLN';
  return `${net.toFixed(2)} ${currency}`;
}

export async function exportExcelReport(route) {
  route = await prepareRouteScreenshots(route);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Dyspozytor24';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Raport');
  setupIndividualSheet(sheet);

  let row = 1;
  row = addReportHeader(sheet, route, row);
  row = addOverallSummary(sheet, route, row);

  for (const section of route.sections || []) {
    row += 1;
    row = addCountrySection(workbook, sheet, route, section, row);
  }

  const fileBase = [
    route.lineNumber || '',
    directionTypeLabel(route.directionType),
    route.carrier || '',
  ].filter(Boolean).join('-') || route.sourceFileName || route.title || 'raport-trasy';
  await downloadWorkbook(workbook, `${sanitize(fileBase.replace(/\.pdf$/i, ''))}.xlsx`);
}

export async function exportMonthlyAggregateReport({ month, carrier = '', routes = [], serviceDays = [] }) {
  routes = await Promise.all((routes || []).map(route => prepareRouteScreenshots(route)));
  const normalizedMonth = /^\d{4}-\d{2}$/.test(String(month || ''))
    ? String(month)
    : new Date().toISOString().slice(0, 7);
  const lineReports = buildMonthlyLineReports(routes, serviceDays, normalizedMonth);

  if (!lineReports.length) {
    throw new Error('Brak zapisanych linii gotowych do raportu miesięcznego.');
  }

  const unidentifiedCurrencies = lineReports.flatMap(line =>
    (line.directionReports || []).flatMap(direction =>
      (direction.tolls || [])
        .filter(toll => toll.hasValue && !toll.currency)
        .map(toll => `${line.lineNumber} ${direction.label}: ${toll.calculatorName}`)
    )
  );
  if (unidentifiedCurrencies.length) {
    throw new Error(`Nie rozpoznano waluty dla: ${unidentifiedCurrencies.join(', ')}. Uzupełnij wynik kalkulatora i zapisz linię ponownie.`);
  }

  const currencies = unique(
    lineReports.flatMap(line =>
      (line.directionReports || []).flatMap(direction =>
        (direction.tolls || []).map(toll => toll.currency)
      )
    ).filter(Boolean)
  );
  const fxRates = await loadNbpRates(currencies, normalizedMonth);
  applyMonthlyAmounts(lineReports, fxRates);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Dyspozytor24';
  workbook.created = new Date();
  workbook.modified = new Date();

  assignAttachmentSheetNames(lineReports);
  addMonthlyInvoiceSummarySheet(workbook, normalizedMonth, carrier, lineReports, fxRates);
  lineReports.forEach((line, index) => {
    addLineAttachmentSheet(workbook, normalizedMonth, line, index + 1, fxRates);
  });

  const suffix = carrier ? `-${carrier}` : '-wszyscy-przewoznicy';
  await downloadWorkbook(workbook, `Raport-zbiorczy-${normalizedMonth}${suffix}.xlsx`);
}

function buildMonthlyLineReports(routes, serviceDays, month) {
  const groups = new Map();
  for (const route of routes || []) {
    const lineNumber = String(route.lineNumber || route.route_number || route.title || '').trim();
    const carrier = String(route.carrier || '').trim().toUpperCase();
    const directionType = normalizeDirectionType(route.directionType || route.direction_type);
    if (!lineNumber || !carrier || !directionType) continue;
    const key = `${carrier}::${lineNumber}`;
    if (!groups.has(key)) groups.set(key, { lineNumber, carrier, routes:[] });
    const group = groups.get(key);
    const existingIndex = group.routes.findIndex(item => normalizeDirectionType(item.directionType || item.direction_type) === directionType);
    if (existingIndex < 0) group.routes.push(route);
    else if (String(route.updatedAt || route.updated_at || '') > String(group.routes[existingIndex].updatedAt || group.routes[existingIndex].updated_at || '')) {
      group.routes[existingIndex] = route;
    }
  }

  const [year, monthNumber] = month.split('-').map(Number);
  const dayCount = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const serviceIndex = new Set(
    (serviceDays || []).map(day => {
      const lineNumber = String(day.route_number || day.lineNumber || '').trim();
      const carrier = String(day.carrier || '').trim().toUpperCase();
      const directionType = normalizeDirectionType(day.direction_type || day.directionType);
      const serviceDate = String(day.service_date || day.serviceDate || '');
      return `${carrier}::${lineNumber}::${serviceDate}::${directionType}`;
    })
  );

  return [...groups.values()]
    .map(group => {
      const outbound = group.routes.find(route => normalizeDirectionType(route.directionType || route.direction_type) === 'outbound');
      const returnRoute = group.routes.find(route => normalizeDirectionType(route.directionType || route.direction_type) === 'return');
      const canonicalRoute = outbound || returnRoute || group.routes[0];

      const days = [];
      for (let day = 1; day <= dayCount; day += 1) {
        const serviceDate = `${month}-${String(day).padStart(2, '0')}`;
        const outboundSelected = serviceIndex.has(`${group.carrier}::${group.lineNumber}::${serviceDate}::outbound`);
        const returnSelected = serviceIndex.has(`${group.carrier}::${group.lineNumber}::${serviceDate}::return`);
        days.push({
          serviceDate,
          outbound:outboundSelected,
          returnRoute:returnSelected,
          runCount:Number(outboundSelected) + Number(returnSelected),
          outboundTotalPln:0,
          returnTotalPln:0,
          totalPln:0,
        });
      }

      const outboundCount = days.filter(day => day.outbound).length;
      const returnCount = days.filter(day => day.returnRoute).length;
      const outboundSource = outbound || canonicalRoute;
      const returnSource = returnRoute || canonicalRoute;

      const outboundRelation = routeRelationLabel(outboundSource, 'outbound', canonicalRoute);
      const returnRelation = routeRelationLabel(returnSource, 'return', outboundSource || canonicalRoute);

      // WAŻNE: stawki infrastrukturalne są wspólnymi stawkami zapisanej linii.
      // Wyjazd i powrót różnią się relacją i liczbą dni kursowania, ale raport
      // NIE może pobierać dwóch różnych historycznych zestawów calculation_results.
      // Wcześniejsza wersja V18.3 brała:
      //   collectRouteTollsForRoute(outboundSource)
      //   collectRouteTollsForRoute(returnSource)
      // co przy starszych/stale zapisanych wersjach mogło dać np.
      // 196,61 PLN w wyjeździe i 140,32 PLN w powrocie dla tego samego e-TOLL.
      // Budujemy jeden aktualny zestaw stawek linii i klonujemy go do obu kierunków.
      const sharedTolls = collectLineTolls(canonicalRoute, group.routes);

      const directionReports = [
        {
          directionType:'outbound',
          label:'WYJAZD',
          relation:outboundRelation,
          route:outboundSource,
          count:outboundCount,
          tolls:cloneTolls(sharedTolls),
          unitTotalPln:0,
          totalPln:0,
        },
        {
          directionType:'return',
          label:'POWRÓT',
          relation:returnRelation,
          route:returnSource,
          count:returnCount,
          tolls:cloneTolls(sharedTolls),
          unitTotalPln:0,
          totalPln:0,
        },
      ];

      return {
        ...group,
        outbound,
        returnRoute,
        canonicalRoute,
        title:canonicalRoute?.title || canonicalRoute?.name || '',
        direction:canonicalRoute?.direction || '',
        countries:unique(group.routes.flatMap(route => route.countries || [])),
        // Pole tolls zostawiamy jako zgodność wsteczną dla starszych funkcji.
        // Rozliczenie miesięczne liczone jest już wyłącznie per kierunek.
        tolls:collectLineTolls(canonicalRoute, group.routes),
        directionReports,
        days,
        outboundCount,
        returnCount,
        runCount:outboundCount + returnCount,
        totalPln:0,
        sheetName:'',
      };
    })
    .sort((a, b) =>
      a.carrier.localeCompare(b.carrier, 'pl')
      || a.lineNumber.localeCompare(b.lineNumber, 'pl', { numeric:true })
    );
}

function collectLineTolls(canonicalRoute, routes) {
  const sourceRoutes = [canonicalRoute, ...(routes || []).filter(route => route !== canonicalRoute)].filter(Boolean);
  const selected = new Map();

  for (const route of sourceRoutes) {
    const routeTolls = new Map();
    for (const section of route.sections || []) {
      const result = route.results?.[section.id];
      const calculatorKey = String(result?.calculatorKey || section.calculatorKey || '').trim();
      if (!calculatorKey && !result) continue;
      const currency = normalizeCurrencyCode(result?.currency || parseCurrencyFromText(result?.costText));
      const calculatorName = calculatorDisplayName(section, result);
      const key = `${calculatorKey || calculatorName}::${currency || 'BRAK'}`;
      if (!routeTolls.has(key)) {
        routeTolls.set(key, {
          key,
          calculatorKey,
          calculatorName,
          countryName:section.countryName || section.countryCode || '',
          currency,
          unitValue:0,
          costTextParts:[],
          hasValue:false,
          rate:currency === 'PLN' ? 1 : null,
          rateDate:'',
          unitValuePln:null,
          monthlyTotalPln:0,
        });
      }
      const toll = routeTolls.get(key);
      const value = reportNetValue(calculatorKey, result?.costValue);
      if (value !== null) {
        toll.unitValue += value;
        toll.hasValue = true;
      }
      if (result?.costText && !toll.costTextParts.includes(result.costText)) toll.costTextParts.push(result.costText);
    }

    for (const [key, toll] of routeTolls) {
      if (!selected.has(key) || (!selected.get(key).hasValue && toll.hasValue)) selected.set(key, toll);
    }
  }

  return [...selected.values()].sort((a, b) =>
    a.countryName.localeCompare(b.countryName, 'pl')
    || a.calculatorName.localeCompare(b.calculatorName, 'pl')
  );
}

function cloneTolls(tolls = []) {
  return (tolls || []).map(toll => ({
    ...toll,
    costTextParts:Array.isArray(toll.costTextParts) ? [...toll.costTextParts] : [],
    // Te pola są przeliczane osobno dla każdego kierunku.
    rate:toll.currency === 'PLN' ? 1 : null,
    rateDate:'',
    unitValuePln:null,
    monthlyTotalPln:0,
  }));
}

function collectRouteTollsForRoute(route) {
  if (!route) return [];
  const tolls = new Map();

  for (const section of route.sections || []) {
    const result = route.results?.[section.id];
    const calculatorKey = String(result?.calculatorKey || section.calculatorKey || '').trim();
    if (!calculatorKey && !result) continue;

    const currency = normalizeCurrencyCode(result?.currency || parseCurrencyFromText(result?.costText));
    const calculatorName = calculatorDisplayName(section, result);
    const key = `${calculatorKey || calculatorName}::${currency || 'BRAK'}`;

    if (!tolls.has(key)) {
      tolls.set(key, {
        key,
        calculatorKey,
        calculatorName,
        countryName:section.countryName || section.countryCode || '',
        currency,
        unitValue:0,
        costTextParts:[],
        hasValue:false,
        rate:currency === 'PLN' ? 1 : null,
        rateDate:'',
        unitValuePln:null,
        monthlyTotalPln:0,
      });
    }

    const toll = tolls.get(key);
    const value = reportNetValue(calculatorKey, result?.costValue);
    if (value !== null) {
      toll.unitValue += value;
      toll.hasValue = true;
    }
    if (result?.costText && !toll.costTextParts.includes(result.costText)) {
      toll.costTextParts.push(result.costText);
    }
  }

  return [...tolls.values()].sort((a, b) =>
    a.countryName.localeCompare(b.countryName, 'pl')
    || a.calculatorName.localeCompare(b.calculatorName, 'pl')
  );
}

function routeRelationLabel(route, directionType, outboundFallback) {
  const routeEndpoints = routeEndpointNames(route);
  const outboundEndpoints = routeEndpointNames(outboundFallback);

  if (directionType === 'return') {
    // Jeżeli zapis powrotu jest kopią wspólnej trasy, odwracamy relację wyjazdu.
    if (
      routeEndpoints
      && outboundEndpoints
      && normalizeRelationPart(routeEndpoints[0]) === normalizeRelationPart(outboundEndpoints[0])
      && normalizeRelationPart(routeEndpoints[1]) === normalizeRelationPart(outboundEndpoints[1])
    ) {
      return `${outboundEndpoints[1]} → ${outboundEndpoints[0]}`;
    }
    if (routeEndpoints) return `${routeEndpoints[0]} → ${routeEndpoints[1]}`;
    if (outboundEndpoints) return `${outboundEndpoints[1]} → ${outboundEndpoints[0]}`;
  }

  if (routeEndpoints) return `${routeEndpoints[0]} → ${routeEndpoints[1]}`;
  if (outboundEndpoints) return `${outboundEndpoints[0]} → ${outboundEndpoints[1]}`;

  const directionText = String(route?.direction || route?.title || route?.name || '').trim();
  const parsed = parseRelationText(directionText);
  if (parsed) {
    return directionType === 'return'
      ? `${parsed[1]} → ${parsed[0]}`
      : `${parsed[0]} → ${parsed[1]}`;
  }
  return directionType === 'return' ? 'Powrót' : 'Wyjazd';
}

function routeEndpointNames(route) {
  if (!route) return null;
  const source = Array.isArray(route.expandedPoints) && route.expandedPoints.length
    ? route.expandedPoints
    : (Array.isArray(route.originalPoints) ? route.originalPoints : []);

  const points = source.filter(point => (
    !point?.isBorder
    && point?.pointType !== 'border'
    && !point?.isRouteConstraint
    && !point?.manualRouteConstraint
    && point?.pointType !== 'route-via'
    && point?.sourceType !== 'manual-route-via'
  ));
  const first = cleanRelationPoint(points[0]?.name);
  const last = cleanRelationPoint(points.at(-1)?.name);
  if (first && last && normalizeRelationPart(first) !== normalizeRelationPart(last)) {
    return [first, last];
  }

  const parsed = parseRelationText(route.direction || route.title || route.name || '');
  return parsed;
}

function parseRelationText(value) {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return null;
  const withoutLine = clean.replace(/^\s*[A-Z]{0,4}\d{2,6}[A-Z]?\s+/i, '').trim();
  const parts = withoutLine
    .split(/\s*(?:→|–|—|\s-\s)\s*/)
    .map(cleanRelationPoint)
    .filter(Boolean);
  if (parts.length < 2) return null;
  return [parts[0], parts.at(-1)];
}

function cleanRelationPoint(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^\s*Granica\s+[A-Z]{2}\s*\/\s*[A-Z]{2}\s*$/i, '')
    .trim();
}

function normalizeRelationPart(value) {
  return String(value || '')
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}


function applyMonthlyAmounts(lineReports, fxRates) {
  for (const line of lineReports) {
    for (const direction of line.directionReports || []) {
      for (const toll of direction.tolls || []) {
        const rateInfo = fxRates.get(toll.currency);
        toll.rate = rateInfo?.rate ?? null;
        toll.rateDate = rateInfo?.effectiveDate || '';
        toll.unitValuePln = toll.hasValue && toll.rate !== null
          ? roundMoney(toll.unitValue * toll.rate)
          : null;
        toll.monthlyTotalPln = toll.unitValuePln === null
          ? 0
          : roundMoney(toll.unitValuePln * direction.count);
      }

      direction.unitTotalPln = roundMoney(
        (direction.tolls || []).reduce((sum, toll) => sum + (toll.unitValuePln || 0), 0)
      );
      direction.totalPln = roundMoney(
        (direction.tolls || []).reduce((sum, toll) => sum + (toll.monthlyTotalPln || 0), 0)
      );
    }

    const outbound = (line.directionReports || []).find(item => item.directionType === 'outbound');
    const returnRoute = (line.directionReports || []).find(item => item.directionType === 'return');

    for (const day of line.days || []) {
      day.outboundTotalPln = day.outbound ? Number(outbound?.unitTotalPln || 0) : 0;
      day.returnTotalPln = day.returnRoute ? Number(returnRoute?.unitTotalPln || 0) : 0;
      day.totalPln = roundMoney(day.outboundTotalPln + day.returnTotalPln);
    }

    line.totalPln = roundMoney(
      (line.directionReports || []).reduce((sum, direction) => sum + (direction.totalPln || 0), 0)
    );
  }
}

async function loadNbpRates(currencies, month) {
  const codes = unique((currencies || []).map(normalizeCurrencyCode).filter(Boolean));
  const rates = new Map();
  rates.set('PLN', {
    currency:'PLN',
    rate:1,
    effectiveDate:`${month}-01`,
    source:'PLN — waluta bazowa',
  });

  const targetDate = monthRateTargetDate(month);
  const startDate = shiftIsoDate(targetDate, -14);
  const missing = [];

  await Promise.all(codes.filter(code => code !== 'PLN').map(async code => {
    try {
      const url = `https://api.nbp.pl/api/exchangerates/rates/A/${encodeURIComponent(code)}/${startDate}/${targetDate}/?format=json`;
      const response = await fetch(url, { headers:{ Accept:'application/json' } });

      // Raport historyczny nigdy nie może spaść do „dzisiejszego” kursu NBP.
      // Jeżeli NBP nie zwróci kursu z zakresu kończącego się na targetDate,
      // przerywamy raport zamiast mieszać miesiące.
      if (!response.ok) {
        throw new Error(`NBP HTTP ${response.status} dla zakresu ${startDate}–${targetDate}`);
      }
      const data = await response.json();
      const quote = Array.isArray(data?.rates) ? data.rates[data.rates.length - 1] : null;
      const rate = finiteNumberOrNull(quote?.mid);
      if (rate === null) throw new Error('Brak kursu średniego');
      rates.set(code, {
        currency:code,
        rate,
        effectiveDate:quote.effectiveDate || targetDate,
        source:`https://api.nbp.pl/api/exchangerates/rates/A/${code}/`,
      });
    } catch (error) {
      console.error(`[D24] Nie udało się pobrać kursu NBP ${code}:`, error);
      missing.push(code);
    }
  }));

  if (missing.length) {
    throw new Error(`Nie udało się pobrać kursów NBP dla: ${missing.join(', ')}. Raport nie został utworzony, aby nie sumować różnych walut błędnie.`);
  }
  return rates;
}

function addMonthlyInvoiceSummarySheet(workbook, month, carrier, lineReports, fxRates) {
  const sheet = workbook.addWorksheet('zestawienie');

  // Pierwszy arkusz odwzorowuje układ przesłanego 202607_infra-UNIBUS:
  // 1) podsumowanie z kolumnami LP. / NAZWA / J.M. / ILOŚĆ / CENA NETTO / WARTOŚĆ NETTO,
  // 2) poniżej — każda linia rozpisana jako osobna strona wydruku,
  // 3) wyjazd i powrót są liczone oddzielnie.
  sheet.views = [{ showGridLines:false }];
  sheet.pageSetup = {
    orientation:'landscape',
    fitToPage:true,
    fitToWidth:1,
    fitToHeight:0,
    horizontalCentered:true,
    margins:{ left:0.25, right:0.25, top:0.35, bottom:0.35, header:0.15, footer:0.15 },
  };
  sheet.properties.defaultRowHeight = 18;

  // Jedne szerokości kolumn dla całego arkusza. Podsumowanie korzysta z B:J,
  // strony poszczególnych linii z A:... zależnie od liczby kalkulatorów.
  const widths = [13, 6, 13, 13, 13, 13, 10, 10, 16, 16];
  for (let index = 0; index < 26; index += 1) {
    sheet.getColumn(index + 1).width = widths[index] || 13;
  }

  const monthEnd = monthEndDate(month);

  // Pola jak w pliku referencyjnym.
  sheet.mergeCells('K2:L2');
  sheet.getCell('K2').value = 'DATA WYSTAWIENIA :';
  sheet.getCell('K2').font = { bold:true, size:10 };
  sheet.getCell('M2').value = monthEnd;
  sheet.getCell('M2').numFmt = 'yyyy-mm-dd';

  sheet.mergeCells('K4:L4');
  sheet.getCell('K4').value = 'DATA SPRZEDAŻY :';
  sheet.getCell('K4').font = { bold:true, size:10 };
  sheet.getCell('M4').value = monthEnd;
  sheet.getCell('M4').numFmt = 'mmmm yy';

  sheet.mergeCells('B12:J12');
  const title = sheet.getCell('B12');
  title.value = 'ZESTAWIENIE';
  title.font = { bold:true, size:16, color:{ argb:'FF111111' } };
  title.alignment = { horizontal:'left', vertical:'middle' };
  sheet.getRow(12).height = 24;

  const headerRowNumber = 19;
  sheet.mergeCells(`C${headerRowNumber}:F${headerRowNumber}`);
  const header = sheet.getRow(headerRowNumber);
  header.getCell(2).value = 'LP.';
  header.getCell(3).value = 'NAZWA USŁUGI / TOWARU';
  header.getCell(7).value = 'J.M.';
  header.getCell(8).value = 'ILOŚĆ';
  header.getCell(9).value = 'CENA NETTO';
  header.getCell(10).value = 'WARTOŚĆ NETTO';

  for (let column = 2; column <= 10; column += 1) {
    const cell = header.getCell(column);
    cell.font = { bold:true, size:10, color:{ argb:'FF111111' } };
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFD9D9D9' } };
    cell.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
    cell.border = referenceTableBorder();
  }
  header.height = 24;

  const summaryRefs = new Map();
  let rowNumber = headerRowNumber + 1;
  let lp = 1;

  for (const line of lineReports) {
    const attachmentNumber = lineReports.indexOf(line) + 1;
    for (const direction of line.directionReports || []) {
      const row = sheet.getRow(rowNumber);
      sheet.mergeCells(`C${rowNumber}:E${rowNumber}`);

      row.getCell(2).value = lp;
      row.getCell(3).value =
        `Koszty infrastrukturalne linia ${line.lineNumber} — ${direction.relation} — ${direction.label}`;
      row.getCell(6).value = `(załącznik nr ${attachmentNumber})`;
      row.getCell(7).value = 'msc';
      row.getCell(8).value = 1;

      // CENA NETTO zostanie po utworzeniu strony linii połączona formułą
      // bezpośrednio z sumą właściwego kierunku, tak jak w pliku wzorcowym.
      row.getCell(9).value = direction.totalPln;
      row.getCell(10).value = {
        formula:`H${rowNumber}*I${rowNumber}`,
        result:direction.totalPln,
      };
      row.getCell(9).numFmt = '#,##0.00';
      row.getCell(10).numFmt = '#,##0.00';

      for (let column = 2; column <= 10; column += 1) {
        const cell = row.getCell(column);
        cell.alignment = {
          horizontal:[2,7,8,9,10].includes(column) ? 'center' : 'left',
          vertical:'middle',
          wrapText:true,
        };
        cell.border = referenceTableBorder();
        if (column === 3 || column === 6) cell.font = { size:10, color:{ argb:'FF111111' } };
      }
      row.height = 26;

      summaryRefs.set(
        `${line.carrier}::${line.lineNumber}::${direction.directionType}`,
        { rowNumber, direction }
      );
      rowNumber += 1;
      lp += 1;
    }
  }

  // W referencji tabela miała 16 pozycji. Jeżeli mamy mniej — zostawiamy
  // puste pozycje; jeżeli mamy więcej kierunków — tabela naturalnie rośnie.
  const minimumPositions = 16;
  while (lp <= minimumPositions) {
    const row = sheet.getRow(rowNumber);
    sheet.mergeCells(`C${rowNumber}:E${rowNumber}`);
    row.getCell(2).value = lp;
    for (let column = 2; column <= 10; column += 1) {
      row.getCell(column).border = referenceTableBorder();
      row.getCell(column).alignment = { vertical:'middle' };
    }
    row.height = 24;
    rowNumber += 1;
    lp += 1;
  }

  sheet.mergeCells(`H${rowNumber}:I${rowNumber}`);
  const totalLabel = sheet.getCell(`H${rowNumber}`);
  totalLabel.value = 'razem  :';
  totalLabel.font = { bold:true, size:12 };
  totalLabel.alignment = { horizontal:'right', vertical:'middle' };
  totalLabel.border = referenceTableBorder();

  const total = roundMoney(
    lineReports.reduce((sum, line) => sum + Number(line.totalPln || 0), 0)
  );
  const firstSummaryDataRow = headerRowNumber + 1;
  const lastSummaryDataRow = Math.max(firstSummaryDataRow, firstSummaryDataRow + summaryRefs.size - 1);
  const totalCell = sheet.getCell(`J${rowNumber}`);
  totalCell.value = summaryRefs.size
    ? { formula:`SUM(J${firstSummaryDataRow}:J${lastSummaryDataRow})`, result:total }
    : 0;
  totalCell.font = { bold:true, size:12 };
  totalCell.numFmt = '#,##0.00';
  totalCell.alignment = { horizontal:'center', vertical:'middle' };
  totalCell.border = referenceTableBorder();
  sheet.getRow(rowNumber).height = 26;

  // Kursy walut pod podsumowaniem — ta sama logika jak w pliku źródłowym,
  // ale wartości pochodzą z kursów NBP użytych faktycznie w obliczeniach.
  rowNumber += 4;
  const rateRows = [...fxRates.values()]
    .filter(info => info.currency !== 'PLN')
    .sort((a, b) => currencyDisplayOrder(a.currency) - currencyDisplayOrder(b.currency));

  for (const info of rateRows) {
    sheet.mergeCells(`C${rowNumber}:D${rowNumber}`);
    sheet.getCell(`C${rowNumber}`).value = exchangeRateLabel(info.currency);
    sheet.getCell(`E${rowNumber}`).value = info.rate;
    sheet.getCell(`E${rowNumber}`).numFmt = '0.000000';
    sheet.getCell(`F${rowNumber}`).value = info.effectiveDate;
    sheet.getCell(`F${rowNumber}`).font = { size:9, color:{ argb:'FF667085' } };
    rowNumber += 1;
  }

  const summaryEndRow = rowNumber + 1;
  addHorizontalPageBreak(sheet, summaryEndRow);

  // Każda linia jest poniżej na tym samym arkuszu, ale zaczyna nową stronę.
  // Dzięki temu nie mamy szerokiego "pasa" kilkudziesięciu linii jak w starym pliku.
  let nextPageRow = summaryEndRow + 2;
  let maxUsedColumn = 13;

  lineReports.forEach((line, index) => {
    const page = addLineBreakdownPage(sheet, month, line, index + 1, nextPageRow);
    maxUsedColumn = Math.max(maxUsedColumn, page.lastColumn);

    for (const directionType of ['outbound', 'return']) {
      const ref = summaryRefs.get(`${line.carrier}::${line.lineNumber}::${directionType}`);
      const targetCell = directionType === 'outbound'
        ? page.outboundTotalCell
        : page.returnTotalCell;
      if (ref && targetCell) {
        sheet.getCell(`I${ref.rowNumber}`).value = {
          formula:`=${targetCell}`,
          result:Number(ref.direction.totalPln || 0),
        };
        sheet.getCell(`I${ref.rowNumber}`).numFmt = '#,##0.00';
      }
    }

    nextPageRow = page.nextRow + 2;
    if (index < lineReports.length - 1) {
      addHorizontalPageBreak(sheet, nextPageRow - 1);
    }
  });

  sheet.pageSetup.printArea = `A1:${columnLetter(maxUsedColumn)}${Math.max(summaryEndRow, nextPageRow - 1)}`;
}

function addLineBreakdownPage(sheet, month, line, attachmentNumber, startRow) {
  const outbound = (line.directionReports || []).find(item => item.directionType === 'outbound') || {
    directionType:'outbound', label:'WYJAZD', relation:'Wyjazd', tolls:[], totalPln:0, count:0
  };
  const returnRoute = (line.directionReports || []).find(item => item.directionType === 'return') || {
    directionType:'return', label:'POWRÓT', relation:'Powrót', tolls:[], totalPln:0, count:0
  };

  const outboundTolls = visibleDirectionTolls(outbound);
  const returnTolls = visibleDirectionTolls(returnRoute);

  const firstTollColumn = 3; // C
  const outboundStart = firstTollColumn;
  const outboundEnd = outboundStart + Math.max(1, outboundTolls.length) - 1;
  const returnStart = outboundEnd + 1;
  const returnEnd = returnStart + Math.max(1, returnTolls.length) - 1;
  const lastColumn = Math.max(8, returnEnd);
  const lastColumnLetter = columnLetter(lastColumn);

  sheet.mergeCells(`A${startRow}:${lastColumnLetter}${startRow}`);
  const title = sheet.getCell(`A${startRow}`);
  title.value =
    `Zestawienie kosztów infrastrukturalnych LINIA ${line.lineNumber} — ${line.title || outbound.relation || ''}`;
  title.font = { bold:true, size:14, color:{ argb:'FF111111' } };
  title.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
  sheet.getRow(startRow).height = 28;

  sheet.mergeCells(`A${startRow + 1}:${lastColumnLetter}${startRow + 1}`);
  const meta = sheet.getCell(`A${startRow + 1}`);
  meta.value =
    `Miesiąc: ${month} · Przewoźnik: ${line.carrier} · załącznik nr ${attachmentNumber}`;
  meta.font = { italic:true, size:10, color:{ argb:'FF667085' } };
  meta.alignment = { horizontal:'center', vertical:'middle' };

  const groupRow = startRow + 3;
  const headerRow = startRow + 4;

  sheet.mergeCells(`A${groupRow}:B${groupRow}`);
  sheet.getCell(`A${groupRow}`).value = 'DATA';

  if (outboundEnd >= outboundStart) {
    sheet.mergeCells(`${columnLetter(outboundStart)}${groupRow}:${columnLetter(outboundEnd)}${groupRow}`);
    sheet.getCell(`${columnLetter(outboundStart)}${groupRow}`).value =
      `DO ${directionDestination(outbound).toUpperCase()}`;
  }
  if (returnEnd >= returnStart) {
    sheet.mergeCells(`${columnLetter(returnStart)}${groupRow}:${columnLetter(returnEnd)}${groupRow}`);
    sheet.getCell(`${columnLetter(returnStart)}${groupRow}`).value =
      `DO ${directionDestination(returnRoute).toUpperCase()}`;
  }

  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = sheet.getRow(groupRow).getCell(column);
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFD9D9D9' } };
    cell.font = { bold:true, size:10 };
    cell.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
    cell.border = referenceTableBorder();
  }
  sheet.getRow(groupRow).height = 22;

  sheet.getCell(`A${headerRow}`).value = 'DATA';
  sheet.getCell(`B${headerRow}`).value = 'DZIEŃ';
  outboundTolls.forEach((toll, index) => {
    sheet.getRow(headerRow).getCell(outboundStart + index).value = calculatorShortName(toll);
  });
  if (!outboundTolls.length) sheet.getRow(headerRow).getCell(outboundStart).value = '—';

  returnTolls.forEach((toll, index) => {
    sheet.getRow(headerRow).getCell(returnStart + index).value = calculatorShortName(toll);
  });
  if (!returnTolls.length) sheet.getRow(headerRow).getCell(returnStart).value = '—';

  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = sheet.getRow(headerRow).getCell(column);
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFD9D9D9' } };
    cell.font = { bold:true, size:10 };
    cell.alignment = { horizontal:'center', vertical:'middle', wrapText:true };
    cell.border = referenceTableBorder();
  }
  sheet.getRow(headerRow).height = 28;

  let rowNumber = headerRow + 1;
  const firstDayRow = rowNumber;

  for (const day of line.days || []) {
    const row = sheet.getRow(rowNumber);
    row.getCell(1).value = parseExcelDate(day.serviceDate);
    row.getCell(1).numFmt = 'yyyy-mm-dd';
    row.getCell(2).value = isoWeekdayNumber(day.serviceDate);

    outboundTolls.forEach((toll, index) => {
      const cell = row.getCell(outboundStart + index);
      cell.value = day.outbound && toll.unitValuePln !== null ? toll.unitValuePln : '';
      cell.numFmt = '#,##0.00';
    });

    returnTolls.forEach((toll, index) => {
      const cell = row.getCell(returnStart + index);
      cell.value = day.returnRoute && toll.unitValuePln !== null ? toll.unitValuePln : '';
      cell.numFmt = '#,##0.00';
    });

    for (let column = 1; column <= lastColumn; column += 1) {
      const cell = row.getCell(column);
      cell.border = referenceTableBorder();
      cell.alignment = {
        horizontal:column === 1 ? 'center' : 'right',
        vertical:'middle',
        wrapText:true,
      };
    }
    row.height = 18;
    rowNumber += 1;
  }

  const lastDayRow = rowNumber - 1;
  const sumRow = rowNumber;
  sheet.mergeCells(`A${sumRow}:B${sumRow}`);
  sheet.getCell(`A${sumRow}`).value = 'SUMA:';
  sheet.getCell(`A${sumRow}`).font = { bold:true };
  sheet.getCell(`A${sumRow}`).alignment = { horizontal:'center', vertical:'middle' };

  outboundTolls.forEach((toll, index) => {
    const column = outboundStart + index;
    const cell = sheet.getRow(sumRow).getCell(column);
    cell.value = {
      formula:`SUM(${columnLetter(column)}${firstDayRow}:${columnLetter(column)}${lastDayRow})`,
      result:Number(toll.monthlyTotalPln || 0),
    };
    cell.numFmt = '#,##0.00';
  });
  returnTolls.forEach((toll, index) => {
    const column = returnStart + index;
    const cell = sheet.getRow(sumRow).getCell(column);
    cell.value = {
      formula:`SUM(${columnLetter(column)}${firstDayRow}:${columnLetter(column)}${lastDayRow})`,
      result:Number(toll.monthlyTotalPln || 0),
    };
    cell.numFmt = '#,##0.00';
  });

  for (let column = 1; column <= lastColumn; column += 1) {
    const cell = sheet.getRow(sumRow).getCell(column);
    cell.border = referenceTableBorder();
    cell.font = { ...(cell.font || {}), bold:true };
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFD9E6F2' } };
    cell.alignment = { horizontal:'right', vertical:'middle' };
  }
  sheet.getCell(`A${sumRow}`).alignment = { horizontal:'center', vertical:'middle' };
  sheet.getRow(sumRow).height = 20;

  // Pod tabelą — "RAZEM e-TOLL", "RAZEM A4" itd. jak w pliku referencyjnym.
  const totalsStart = sumRow + 2;
  const totals = combinedCalculatorTotals(outbound, returnRoute);
  const labelColumn = Math.max(3, lastColumn - 2);
  const valueColumn = lastColumn;

  let totalsRow = totalsStart;
  for (const total of totals) {
    if (labelColumn < valueColumn) {
      sheet.mergeCells(
        `${columnLetter(labelColumn)}${totalsRow}:${columnLetter(valueColumn - 1)}${totalsRow}`
      );
    }
    const labelCell = sheet.getRow(totalsRow).getCell(labelColumn);
    labelCell.value = `RAZEM ${total.label}`;
    labelCell.alignment = { horizontal:'right', vertical:'middle' };
    labelCell.border = referenceTableBorder();

    const valueCell = sheet.getRow(totalsRow).getCell(valueColumn);
    valueCell.value = total.total;
    valueCell.numFmt = '#,##0.00';
    valueCell.border = referenceTableBorder();
    totalsRow += 1;
  }

  if (labelColumn < valueColumn) {
    sheet.mergeCells(
      `${columnLetter(labelColumn)}${totalsRow}:${columnLetter(valueColumn - 1)}${totalsRow}`
    );
  }
  const netLabel = sheet.getRow(totalsRow).getCell(labelColumn);
  netLabel.value = 'SUMA NETTO';
  netLabel.font = { bold:true, size:11 };
  netLabel.alignment = { horizontal:'right', vertical:'middle' };
  netLabel.border = referenceTableBorder();
  netLabel.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFF2F1E8' } };

  const netValue = sheet.getRow(totalsRow).getCell(valueColumn);
  netValue.value = {
    formula:`SUM(${columnLetter(outboundStart)}${sumRow}:${columnLetter(returnEnd)}${sumRow})`,
    result:Number(line.totalPln || 0),
  };
  netValue.numFmt = '#,##0.00';
  netValue.font = { bold:true, size:11 };
  netValue.border = referenceTableBorder();
  netValue.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFF2F1E8' } };

  const outboundTotalCell = `${columnLetter(valueColumn)}${totalsRow + 2}`;
  const returnTotalCell = `${columnLetter(valueColumn)}${totalsRow + 3}`;

  // Dwie pomocnicze sumy kierunków są widoczne — dzięki temu można łatwo
  // zweryfikować pierwszą tabelę i nie ma już jednej wspólnej komórki.
  if (labelColumn < valueColumn) {
    sheet.mergeCells(
      `${columnLetter(labelColumn)}${totalsRow + 2}:${columnLetter(valueColumn - 1)}${totalsRow + 2}`
    );
    sheet.mergeCells(
      `${columnLetter(labelColumn)}${totalsRow + 3}:${columnLetter(valueColumn - 1)}${totalsRow + 3}`
    );
  }
  sheet.getRow(totalsRow + 2).getCell(labelColumn).value = `WYJAZD — ${outbound.relation}`;
  sheet.getRow(totalsRow + 2).getCell(labelColumn).font = { bold:true };
  sheet.getRow(totalsRow + 2).getCell(valueColumn).value = Number(outbound.totalPln || 0);
  sheet.getRow(totalsRow + 2).getCell(valueColumn).numFmt = '#,##0.00';

  sheet.getRow(totalsRow + 3).getCell(labelColumn).value = `POWRÓT — ${returnRoute.relation}`;
  sheet.getRow(totalsRow + 3).getCell(labelColumn).font = { bold:true };
  sheet.getRow(totalsRow + 3).getCell(valueColumn).value = Number(returnRoute.totalPln || 0);
  sheet.getRow(totalsRow + 3).getCell(valueColumn).numFmt = '#,##0.00';

  for (const rowIndex of [totalsRow + 2, totalsRow + 3]) {
    for (let column = labelColumn; column <= valueColumn; column += 1) {
      sheet.getRow(rowIndex).getCell(column).border = referenceTableBorder();
    }
  }

  return {
    nextRow:totalsRow + 4,
    outboundTotalCell,
    returnTotalCell,
    lineTotalCell:`${columnLetter(valueColumn)}${totalsRow}`,
    lastColumn,
  };
}

function visibleDirectionTolls(direction) {
  const tolls = (direction?.tolls || []).filter(toll => toll && (toll.hasValue || toll.calculatorKey));
  return tolls;
}

function combinedCalculatorTotals(outbound, returnRoute) {
  const totals = new Map();
  for (const direction of [outbound, returnRoute]) {
    for (const toll of direction?.tolls || []) {
      const key = String(toll.calculatorKey || toll.calculatorName || '').trim();
      if (!key) continue;
      if (!totals.has(key)) {
        totals.set(key, {
          key,
          label:calculatorShortName(toll),
          total:0,
        });
      }
      totals.get(key).total = roundMoney(
        totals.get(key).total + Number(toll.monthlyTotalPln || 0)
      );
    }
  }
  return [...totals.values()];
}

function calculatorShortName(toll) {
  const key = String(toll?.calculatorKey || '').trim();
  return ({
    'etoll-pl':'e-TOLL',
    'a1-amberone-pl':'A1',
    'a2-awsa-pl':'A2',
    'a4-sam-pl':'A4',
    'myto-cz':'MYTO-CZ',
    'emyto-sk':'MYTO-SK',
    'asfinag-at':'MAUT-A',
    'hugo-hu':'WĘGRY',
    'darsgo-si':'Słowenia',
    'hac-hr':'Chorwacja',
    'azm-hr':'Chorwacja',
    'bina-istra-hr':'Chorwacja',
    'autostrade-it':'Włochy',
    'autoroutes-fr':'Francja',
  })[key] || String(toll?.calculatorName || key || 'INNE');
}

function directionDestination(direction) {
  const relation = String(direction?.relation || '').trim();
  const parts = relation.split(/\s*→\s*/).map(part => part.trim()).filter(Boolean);
  return parts[1] || relation || direction?.label || '';
}

function isoWeekdayNumber(value) {
  const date = parseExcelDate(value);
  if (!date) return '';
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

function monthEndDate(month) {
  const [year, monthNumber] = String(month || '').split('-').map(Number);
  return year && monthNumber
    ? new Date(year, monthNumber, 0, 12, 0, 0)
    : new Date();
}

function exchangeRateLabel(currency) {
  return ({
    CZK:'Kurs Korony Czeskiej',
    EUR:'Kurs Euro',
    CHF:'Kurs Franka Szwajcarskiego',
    HUF:'Kurs Forinta Węgierskiego',
    GBP:'Kurs Funta Brytyjskiego',
    USD:'Kurs Dolara Amerykańskiego',
    HRK:'Kurs Kuny Chorwackiej',
    RON:'Kurs Leja Rumuńskiego',
    BGN:'Kurs Lewa Bułgarskiego',
  })[currency] || `Kurs ${currency}`;
}

function currencyDisplayOrder(currency) {
  return ({ CZK:1, EUR:2, CHF:3, HUF:4, GBP:5, USD:6 })[currency] || 50;
}

function addHorizontalPageBreak(sheet, rowNumber) {
  if (!rowNumber || rowNumber < 1) return;
  try {
    if (typeof sheet.addRowBreak === 'function') {
      sheet.addRowBreak(rowNumber);
    }
  } catch (error) {
    console.warn('[D24 REPORT] Nie udało się dodać podziału strony:', error);
  }
}

function addLineCalendarSummaryBlock(sheet, line, attachmentNumber, startRow) {
  const lastColumn = 8;
  const lastColumnLetter = columnLetter(lastColumn);

  sheet.mergeCells(`A${startRow}:${lastColumnLetter}${startRow}`);
  const titleCell = sheet.getCell(`A${startRow}`);
  titleCell.value = {
    text:`LINIA ${line.lineNumber} — ${line.title || 'trasa zapisana w Dyspozytor24'} · ZAŁĄCZNIK NR ${attachmentNumber}`,
    hyperlink:`#'${escapeSheetNameForFormula(line.sheetName)}'!A1`,
  };
  titleCell.font = { bold:true, size:13, color:{ argb:'FFFFFFFF' }, underline:true };
  titleCell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0052A5' } };
  titleCell.alignment = { vertical:'middle', horizontal:'left', wrapText:true };
  sheet.getRow(startRow).height = 26;

  sheet.mergeCells(`A${startRow + 1}:${lastColumnLetter}${startRow + 1}`);
  sheet.getCell(`A${startRow + 1}`).value =
    `Przewoźnik: ${line.carrier} · Wyjazdy: ${line.outboundCount} · Powroty: ${line.returnCount} · Razem przejazdów: ${line.runCount} · Wartość miesiąca: ${formatMoney(line.totalPln)} PLN`;
  sheet.getCell(`A${startRow + 1}`).font = { italic:true, color:{ argb:'FF475467' } };
  sheet.getCell(`A${startRow + 1}`).alignment = { vertical:'middle', wrapText:true };

  const headerRowNumber = startRow + 3;
  const headerRow = sheet.getRow(headerRowNumber);
  ['Lp.', 'Data', 'Dzień', 'Wyjazd', 'Powrót', 'Wyjazd [PLN]', 'Powrót [PLN]', 'Razem [PLN]']
    .forEach((value, index) => { headerRow.getCell(index + 1).value = value; });
  styleDarkHeader(headerRow);
  headerRow.height = 36;

  let rowNumber = headerRowNumber + 1;
  line.days.forEach((day, index) => {
    const row = sheet.getRow(rowNumber);
    row.getCell(1).value = index + 1;
    row.getCell(2).value = parseExcelDate(day.serviceDate);
    row.getCell(2).numFmt = 'dd.mm.yyyy';
    row.getCell(3).value = weekdayLabel(day.serviceDate);
    row.getCell(4).value = day.outbound ? 'TAK' : '';
    row.getCell(5).value = day.returnRoute ? 'TAK' : '';
    row.getCell(6).value = day.outboundTotalPln || 0;
    row.getCell(7).value = day.returnTotalPln || 0;
    row.getCell(8).value = day.totalPln || 0;
    for (let column = 6; column <= 8; column += 1) row.getCell(column).numFmt = '#,##0.00';
    row.alignment = { vertical:'middle', horizontal:'center', wrapText:true };
    if (day.runCount === 0) row.font = { color:{ argb:'FF98A2B3' } };
    rowNumber += 1;
  });

  const summaryRow = sheet.getRow(rowNumber);
  sheet.mergeCells(`A${rowNumber}:C${rowNumber}`);
  summaryRow.getCell(1).value = 'PODSUMOWANIE MIESIĄCA';
  summaryRow.getCell(4).value = line.outboundCount;
  summaryRow.getCell(5).value = line.returnCount;
  const outbound = (line.directionReports || []).find(item => item.directionType === 'outbound');
  const returnRoute = (line.directionReports || []).find(item => item.directionType === 'return');
  summaryRow.getCell(6).value = outbound?.totalPln || 0;
  summaryRow.getCell(7).value = returnRoute?.totalPln || 0;
  summaryRow.getCell(8).value = line.totalPln;
  for (let column = 6; column <= 8; column += 1) summaryRow.getCell(column).numFmt = '#,##0.00 "PLN"';
  summaryRow.font = { bold:true };
  summaryRow.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FFD9EAF7' } };
  summaryRow.alignment = { vertical:'middle', horizontal:'center', wrapText:true };

  applyBorders(sheet, headerRowNumber, rowNumber, lastColumn);
  return rowNumber + 1;
}

function addLineAttachmentSheet(workbook, month, line, attachmentNumber, fxRates) {
  const sheet = workbook.addWorksheet(line.sheetName);
  setupIndividualSheet(sheet);
  sheet.views = [{ state:'frozen', ySplit:1 }];
  sheet.pageSetup = {
    orientation:'portrait',
    fitToPage:false,
    margins:{ left:0.35, right:0.35, top:0.5, bottom:0.5, header:0.2, footer:0.2 },
  };

  let rowNumber = 1;
  sheet.mergeCells(`A${rowNumber}:L${rowNumber}`);
  sheet.getCell(`A${rowNumber}`).value = {
    text:`ZAŁĄCZNIK NR ${attachmentNumber} — LINIA ${line.lineNumber}`,
    hyperlink:"#'zestawienie'!B12",
  };
  sheet.getCell(`A${rowNumber}`).font = {
    bold:true,
    size:16,
    color:{ argb:'FF0052A5' },
    underline:true,
  };
  sheet.getCell(`A${rowNumber}`).alignment = { horizontal:'center', vertical:'middle' };
  sheet.getRow(rowNumber).height = 28;

  rowNumber += 1;
  sheet.mergeCells(`A${rowNumber}:L${rowNumber}`);
  sheet.getCell(`A${rowNumber}`).value =
    `Miesiąc: ${month} · Przewoźnik: ${line.carrier} · Wyjazdy: ${line.outboundCount} · Powroty: ${line.returnCount} · Razem przejazdów: ${line.runCount} · Wartość miesiąca: ${formatMoney(line.totalPln)} PLN`;
  sheet.getCell(`A${rowNumber}`).alignment = { horizontal:'center', vertical:'middle', wrapText:true };
  sheet.getCell(`A${rowNumber}`).font = { italic:true, color:{ argb:'FF475467' } };

  for (const direction of line.directionReports || []) {
    rowNumber += 1;
    sheet.mergeCells(`A${rowNumber}:L${rowNumber}`);
    sheet.getCell(`A${rowNumber}`).value =
      `${direction.label}: ${direction.relation} · ${direction.count} przejazdów · ${formatMoney(direction.totalPln)} PLN`;
    sheet.getCell(`A${rowNumber}`).font = { bold:true, color:{ argb:direction.directionType === 'outbound' ? 'FF0052A5' : 'FF344054' } };
    sheet.getCell(`A${rowNumber}`).alignment = { horizontal:'left', vertical:'middle', wrapText:true };
  }

  rowNumber += 2;
  rowNumber = addLineUnitRatesTable(sheet, line, rowNumber);

  const evidenceRoute = buildMonthlyEvidenceRoute(line);
  rowNumber += 2;
  rowNumber = addReportHeader(sheet, evidenceRoute, rowNumber);
  rowNumber = addOverallSummary(sheet, evidenceRoute, rowNumber);

  for (const section of evidenceRoute.sections || []) {
    rowNumber += 1;
    rowNumber = addCountrySection(workbook, sheet, evidenceRoute, section, rowNumber);
  }
}

function addLineUnitRatesTable(sheet, line, startRow) {
  sheet.mergeCells(`A${startRow}:L${startRow}`);
  sheet.getCell(`A${startRow}`).value = 'ROZLICZENIE KIERUNKÓW — STAWKI I WARTOŚĆ MIESIĘCZNA';
  sheet.getCell(`A${startRow}`).font = { bold:true, color:{ argb:'FFFFFFFF' } };
  sheet.getCell(`A${startRow}`).fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0B67BF' } };

  const headerRowNumber = startRow + 1;
  const unitHeader = sheet.getRow(headerRowNumber);
  [
    'Kierunek',
    'Relacja',
    'Opłata / kalkulator',
    'Kraj',
    'Kwota',
    'Waluta',
    'Kurs NBP',
    'Data kursu',
    'Stawka 1 przejazdu [PLN]',
    'Liczba przejazdów',
    'Wartość miesiąca [PLN]',
  ].forEach((value, index) => { unitHeader.getCell(index + 1).value = value; });
  styleHeader(unitHeader);

  let rowNumber = headerRowNumber;
  for (const direction of line.directionReports || []) {
    const tolls = Array.isArray(direction.tolls) && direction.tolls.length
      ? direction.tolls
      : [{
          calculatorName:'Brak zapisanych opłat',
          countryName:'',
          unitValue:0,
          currency:'',
          rate:null,
          rateDate:'',
          unitValuePln:0,
          monthlyTotalPln:0,
          hasValue:false,
        }];

    for (const toll of tolls) {
      rowNumber += 1;
      const row = sheet.getRow(rowNumber);
      row.getCell(1).value = direction.label;
      row.getCell(2).value = direction.relation;
      row.getCell(3).value = toll.hasValue
        ? toll.calculatorName
        : `${toll.calculatorName} — BRAK WYNIKU`;
      if (!toll.hasValue) row.getCell(3).font = { bold:true, color:{ argb:'FFB42318' } };
      row.getCell(4).value = toll.countryName;
      row.getCell(5).value = toll.hasValue ? toll.unitValue : '';
      row.getCell(5).numFmt = '#,##0.00';
      row.getCell(6).value = toll.currency;
      row.getCell(7).value = toll.rate ?? '';
      row.getCell(7).numFmt = '0.000000';
      row.getCell(8).value = toll.rateDate;
      row.getCell(9).value = toll.unitValuePln ?? '';
      row.getCell(9).numFmt = '#,##0.00';
      row.getCell(10).value = direction.count;
      row.getCell(11).value = toll.monthlyTotalPln ?? 0;
      row.getCell(11).numFmt = '#,##0.00';
      row.alignment = { vertical:'middle', wrapText:true };
    }
  }

  rowNumber += 1;
  const totalRow = sheet.getRow(rowNumber);
  sheet.mergeCells(`A${rowNumber}:J${rowNumber}`);
  totalRow.getCell(1).value = 'RAZEM LINIA';
  totalRow.getCell(1).font = { bold:true };
  totalRow.getCell(1).alignment = { horizontal:'right' };
  totalRow.getCell(11).value = line.totalPln;
  totalRow.getCell(11).numFmt = '#,##0.00 "PLN"';
  totalRow.getCell(11).font = { bold:true };

  applyBorders(sheet, headerRowNumber, rowNumber, 11);
  return rowNumber + 1;
}

function buildMonthlyEvidenceRoute(line) {
  const route = line.canonicalRoute || line.outbound || line.returnRoute || {};
  return {
    ...route,
    lineNumber:line.lineNumber,
    title:line.title || route.title || route.name || `Linia ${line.lineNumber}`,
    carrier:line.carrier,
    directionType:'both',
    direction:route.direction || 'Wyjazd + powrót — wspólna trasa i wspólne stawki',
    countries:line.countries,
    sections:Array.isArray(route.sections) ? route.sections : [],
    results:route.results || {},
  };
}

function addMonthlyRouteEvidence(workbook, sheet, route, startRow, month, line) {
  if (!route) return startRow;
  addSectionBand(sheet, startRow, 'OPIS PUNKTÓW TRASY I ZAŁĄCZNIKI Z KALKULATORÓW');
  let row = startRow + 1;
  const metadataRows = [
    ['Nr linii', line.lineNumber],
    ['Nazwa linii z PDF', route.title || line.title || ''],
    ['Przewoźnik', line.carrier],
    ['Rozliczany miesiąc', month],
    ['Dane kierunków', 'Wyjazd + powrót — wspólna trasa i wspólne stawki'],
    ['Plik źródłowy', route.sourceFileName || ''],
    ['Dystans całej linii [km]', round((Number(route.distanceMeters) || 0) / 1000)],
    ['Czas całej linii', formatDuration(route.durationSeconds || 0)],
    ['Państwa na trasie', line.countries.join(', ')],
  ];
  for (const [label, value] of metadataRows) {
    sheet.getCell(`A${row}`).value = label;
    sheet.getCell(`A${row}`).font = { bold:true };
    sheet.getCell(`B${row}`).value = value;
    sheet.mergeCells(`B${row}:F${row}`);
    sheet.getCell(`B${row}`).alignment = { wrapText:true, vertical:'top' };
    row += 1;
  }

  row += 1;
  const pointHeader = sheet.getRow(row);
  ['Lp.', 'Punkt', 'Typ', 'GPS szer.', 'GPS dł.', 'Państwo']
    .forEach((value, index) => { pointHeader.getCell(index + 1).value = value; });
  styleHeader(pointHeader);
  row += 1;
  for (const [index, point] of (route.expandedPoints || []).entries()) {
    const type = point?.isBorder
      ? 'Granica'
      : (point?.isForced || point?.pointType === 'forced' || point?.forceRoute ? 'Wymuszony przebieg' : 'Przystanek');
    const excelRow = sheet.getRow(row);
    excelRow.getCell(1).value = index + 1;
    excelRow.getCell(2).value = point?.name || '';
    excelRow.getCell(3).value = type;
    excelRow.getCell(4).value = normalizeNumber(point?.lat);
    excelRow.getCell(5).value = normalizeNumber(point?.lon);
    excelRow.getCell(6).value = point?.countryName || point?.countryCode || '';
    excelRow.alignment = { vertical:'top', wrapText:true };
    row += 1;
  }
  applyBorders(sheet, pointHeader.number, Math.max(pointHeader.number, row - 1), 6);

  for (const section of route.sections || []) {
    row += 1;
    row = addCountrySection(workbook, sheet, route, section, row);
  }
  return row;
}

function assignAttachmentSheetNames(lineReports) {
  const used = new Set(['zestawienie']);
  for (const line of lineReports) {
    const base = sanitizeSheetName(line.lineNumber || 'Linia').slice(0, 31) || 'Linia';
    let candidate = base;
    if (used.has(candidate.toLowerCase())) {
      const suffix = `-${line.carrier}`;
      candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    }
    let index = 2;
    while (used.has(candidate.toLowerCase())) {
      const suffix = `-${index}`;
      candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
      index += 1;
    }
    used.add(candidate.toLowerCase());
    line.sheetName = candidate;
  }
}

function monthRateTargetDate(month) {
  const [year, monthNumber] = String(month).split('-').map(Number);
  const monthEnd = new Date(Date.UTC(year, monthNumber, 0, 12, 0, 0));
  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0));
  return (monthEnd > todayUtc ? todayUtc : monthEnd).toISOString().slice(0, 10);
}

function shiftIsoDate(value, days) {
  const [year, month, day] = String(value).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  return date.toISOString().slice(0, 10);
}

function normalizeCurrencyCode(value) {
  const text = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  return ({
    'ZŁ':'PLN', 'ZL':'PLN', 'PLN':'PLN',
    '€':'EUR', 'EUR':'EUR',
    'KČ':'CZK', 'KC':'CZK', 'CZK':'CZK',
    'FT':'HUF', 'HUF':'HUF',
    'CHF':'CHF', 'HRK':'HRK', 'RON':'RON', 'BGN':'BGN', 'GBP':'GBP', 'USD':'USD',
  })[text] || (/^[A-Z]{3}$/.test(text) ? text : '');
}

function parseCurrencyFromText(value) {
  const text = String(value || '').toUpperCase();
  return text.match(/\b(PLN|EUR|CZK|HUF|CHF|HRK|RON|BGN|GBP|USD)\b/)?.[1]
    || (text.includes('€') ? 'EUR' : text.includes('KČ') ? 'CZK' : text.includes('ZŁ') ? 'PLN' : '');
}

function columnLetter(number) {
  let value = Math.max(1, Number(number) || 1);
  let result = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
}

function sanitizeSheetName(value) {
  return String(value || '').replace(/[\\/*?:\[\]]/g, '_').trim();
}

function escapeSheetNameForFormula(value) {
  return String(value || '').replace(/'/g, "''");
}

function referenceTableBorder() {
  return {
    top:{ style:'thin', color:{ argb:'FF111111' } },
    left:{ style:'thin', color:{ argb:'FF111111' } },
    bottom:{ style:'thin', color:{ argb:'FF111111' } },
    right:{ style:'thin', color:{ argb:'FF111111' } },
  };
}

function styleDarkHeader(row) {
  row.font = { bold:true, color:{ argb:'FFFFFFFF' } };
  row.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF252525' } };
  row.alignment = { vertical:'middle', horizontal:'center', wrapText:true };
  row.height = 30;
}

function setupIndividualSheet(sheet) {
  sheet.properties.defaultRowHeight = 18;
  sheet.pageSetup = {
    orientation:'portrait',
    fitToPage:false,
    margins:{ left:0.35, right:0.35, top:0.5, bottom:0.5, header:0.2, footer:0.2 },
  };
  sheet.columns = [
    { width:8 }, { width:28 }, { width:18 }, { width:16 }, { width:16 }, { width:22 },
    { width:18 }, { width:22 }, { width:18 }, { width:18 }, { width:18 }, { width:18 },
  ];
}

function addReportHeader(sheet, route, startRow) {
  sheet.mergeCells(`A${startRow}:L${startRow}`);
  const title = sheet.getCell(`A${startRow}`);
  title.value = route.title || route.sourceFileName || 'Raport trasy';
  title.font = { bold:true, size:18, color:{ argb:'FF0052A5' } };
  title.alignment = { vertical:'middle' };
  sheet.getRow(startRow).height = 28;

  sheet.mergeCells(`A${startRow + 1}:L${startRow + 1}`);
  const sub = sheet.getCell(`A${startRow + 1}`);
  sub.value = 'Podsumowanie linii oraz rozliczenia wszystkich krajów w jednym arkuszu';
  sub.font = { italic:true, size:11, color:{ argb:'FF64748B' } };
  return startRow + 3;
}

function addOverallSummary(sheet, route, startRow) {
  addSectionBand(sheet, startRow, 'Podsumowanie linii');
  const countries = unique((route.sections || []).map(section => section.countryName).filter(Boolean));
  const totals = totalsByCurrency(route);
  const rows = [
    ['Nr linii', route.lineNumber || route.title || ''],
    ['Nazwa linii z PDF', route.title || ''],
    ['Przewoźnik', route.carrier || ''],
    ['Typ kursu', directionTypeLabel(route.directionType)],
    ['Plik źródłowy', route.sourceFileName || ''],
    ['Kierunek', route.direction || ''],
    ['Liczba punktów wejściowych', Array.isArray(route.originalPoints) ? route.originalPoints.length : 0],
    ['Liczba punktów po wyznaczeniu trasy', Array.isArray(route.expandedPoints) ? route.expandedPoints.length : 0],
    ['Liczba punktów granicznych', (route.expandedPoints || []).filter(point => point.isBorder).length],
    ['Liczba punktów wymuszających', (route.expandedPoints || []).filter(point => point.isForced || point.pointType === 'forced' || point.forceRoute).length],
    ['Dystans całej linii [km]', round((Number(route.distanceMeters) || 0) / 1000)],
    ['Czas całej linii', formatDuration(route.durationSeconds || 0)],
    ['Państwa na trasie', countries.join(', ')],
    ['Suma opłat', totals.length ? totals.map(item => `${formatMoney(item.total)} ${item.currency}`).join(' + ') : 'Brak obliczonych opłat'],
    ['Data raportu', new Date().toLocaleString('pl-PL')],
  ];

  let row = startRow + 1;
  for (const [label, value] of rows) {
    sheet.getCell(`A${row}`).value = label;
    sheet.getCell(`A${row}`).font = { bold:true };
    sheet.getCell(`B${row}`).value = value;
    sheet.mergeCells(`B${row}:F${row}`);
    row += 1;
  }

  row += 1;
  const header = sheet.addRow([]);
  header.getCell(1).value = 'Lp.';
  header.getCell(2).value = 'Nazwa';
  header.getCell(3).value = 'Typ';
  header.getCell(4).value = 'GPS szer.';
  header.getCell(5).value = 'GPS dł.';
  header.getCell(6).value = 'Państwo';
  styleHeader(header);
  row = header.number + 1;

  for (const [index, point] of (route.expandedPoints || []).entries()) {
    const excelRow = sheet.addRow([
      index + 1,
      point?.name || '',
      point?.isBorder ? 'Granica' : (point?.isForced || point?.pointType === 'forced' || point?.forceRoute ? 'Wymuszony przebieg' : 'Przystanek'),
      normalizeNumber(point?.lat),
      normalizeNumber(point?.lon),
      point?.countryName || point?.countryCode || '',
    ]);
    excelRow.getCell(2).alignment = { wrapText:true, vertical:'top' };
    row = excelRow.number + 1;
  }
  return row;
}

function addCountrySection(workbook, sheet, route, section, startRow) {
  const result = route.results?.[section.id];
  const calculatorName = calculatorDisplayName(section, result);
  const screenshot = pickCalculatorScreenshot(result);
  const distanceKm = getSectionDistanceKm(section, result);
  const duration = result?.durationText || (Number.isFinite(Number(section.durationSeconds)) ? formatDuration(Number(section.durationSeconds)) : '');
  const settingsText = result?.settingsSummary || result?.vehicleSettingsText || formatSettings(result?.settings);

  addSectionBand(sheet, startRow, `${section.order}. ${section.displayName || section.countryName || section.countryCode || 'Kraj'} — ${calculatorName}`);
  let row = startRow + 1;
  const summaryRows = [
    ['Kalkulator', calculatorName],
    ['Status', result ? 'Obliczono' : 'Oczekuje'],
    [reportVatLabel(String(result?.calculatorKey || section?.calculatorKey || '')), reportCostText(section, result)],
    ['Waluta', result?.currency || ''],
    ['Dystans odcinka [km]', distanceKm ?? ''],
    ['Czas odcinka', duration],
    ['Wariant', result?.variantLabel || 'Najszybsza'],
  ];
  if (result?.grossCostText && reportVatRate(String(result?.calculatorKey || section?.calculatorKey || '')) === null) summaryRows.splice(3, 0, ['Koszt brutto', result.grossCostText]);
  if (settingsText) summaryRows.push(['Parametry pojazdu', settingsText]);

  for (const [label, value] of summaryRows) {
    sheet.getCell(`A${row}`).value = label;
    sheet.getCell(`A${row}`).font = { bold:true };
    sheet.getCell(`B${row}`).value = value;
    sheet.mergeCells(`B${row}:H${row}`);
    sheet.getCell(`B${row}`).alignment = { wrapText:true, vertical:'top' };
    row += 1;
  }

  row += 1;
  const tableHeader = sheet.addRow([]);
  tableHeader.getCell(1).value = 'Lp.';
  tableHeader.getCell(2).value = 'Punkt';
  tableHeader.getCell(3).value = 'Typ';
  tableHeader.getCell(4).value = 'GPS szer.';
  tableHeader.getCell(5).value = 'GPS dł.';
  tableHeader.getCell(6).value = 'Państwo';
  styleHeader(tableHeader);
  row = tableHeader.number + 1;

  for (const [index, point] of (section.points || []).entries()) {
    const type = point?.isBorder ? 'Granica' : (point?.isForced || point?.pointType === 'forced' || point?.forceRoute ? 'Wymuszony przebieg' : 'Przystanek');
    const excelRow = sheet.addRow([
      index + 1,
      point?.name || '',
      type,
      normalizeNumber(point?.lat),
      normalizeNumber(point?.lon),
      point?.countryName || point?.countryCode || '',
    ]);
    excelRow.getCell(2).alignment = { wrapText:true, vertical:'top' };
    row = excelRow.number + 1;
  }

  if (screenshot) {
    row += 1;
    const imageTopRow = row;
    const imageInfo = addImagePreservingOriginalSize(
      workbook,
      sheet,
      screenshot,
      `A${imageTopRow}`,
      { maxWidth:900, maxHeight:820 }
    );
    const rowsUsed = Math.max(8, imageInfo?.rowsUsed || 18);
    const imageBottomRow = imageTopRow + rowsUsed;
    sheet.getCell(`A${imageBottomRow}`).value = `Zrzut kalkulatora ${calculatorName}`;
    sheet.getCell(`A${imageBottomRow}`).font = { italic:true, size:10, color:{ argb:'FF475467' } };
    row = imageBottomRow + 2;
  }
  return row;
}

function addWorkbookTitle(sheet, titleText, subtitleText) {
  sheet.mergeCells('A1:O1');
  sheet.getCell('A1').value = titleText;
  sheet.getCell('A1').font = { bold:true, size:18, color:{ argb:'FF0052A5' } };
  sheet.getRow(1).height = 28;
  sheet.mergeCells('A2:O2');
  sheet.getCell('A2').value = subtitleText;
  sheet.getCell('A2').font = { italic:true, color:{ argb:'FF64748B' } };
  sheet.addRow([]);
}

function addSectionBand(sheet, row, text) {
  sheet.mergeCells(`A${row}:L${row}`);
  const cell = sheet.getCell(`A${row}`);
  cell.value = text;
  cell.font = { bold:true, size:13, color:{ argb:'FFFFFFFF' } };
  cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0052A5' } };
  cell.alignment = { vertical:'middle' };
  sheet.getRow(row).height = 22;
}

function styleHeader(row) {
  row.font = { bold:true, color:{ argb:'FFFFFFFF' } };
  row.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0B67BF' } };
  row.alignment = { vertical:'middle', wrapText:true };
  row.height = 30;
}

function applyBorders(sheet, startRow, endRow, columnCount) {
  for (let rowNumber = startRow; rowNumber <= endRow; rowNumber += 1) {
    const row = sheet.getRow(rowNumber);
    for (let column = 1; column <= columnCount; column += 1) {
      row.getCell(column).border = {
        top:{ style:'thin', color:{ argb:'FFE2E8F0' } },
        left:{ style:'thin', color:{ argb:'FFE2E8F0' } },
        bottom:{ style:'thin', color:{ argb:'FFE2E8F0' } },
        right:{ style:'thin', color:{ argb:'FFE2E8F0' } },
      };
    }
  }
}

function calculatorDisplayName(section, result) {
  if (result?.calculatorName) return result.calculatorName;
  const key = result?.calculatorKey || section?.calculatorKey || ({
    PL:'etoll-pl', CZ:'myto-cz', AT:'asfinag-at', HU:'hugo-hu', SK:'emyto-sk', SI:'darsgo-si',
  }[section?.countryCode] || null);
  return ({
    'etoll-pl':'e-TOLL Polska',
    'myto-cz':'MYTO CZ',
    'asfinag-at':'ASFINAG Austria',
    'hugo-hu':'HU-GO Węgry',
    'emyto-sk':'eMyto Słowacja',
    'darsgo-si':'DarsGo Słowenia',
    'a1-amberone-pl':'A1 AmberOne Rusocin–Nowa Wieś',
    'a2-awsa-pl':'A2 Autostrada Wielkopolska',
    'a4-sam-pl':'A4 Katowice–Kraków',
    'hac-hr':'HAC — płatne autostrady Chorwacji',
    'azm-hr':'Autostrada Zagreb–Macelj',
    'bina-istra-hr':'Bina-Istra — Istarski ipsilon',
    'autostrade-it':'Autostrade per l’Italia',
    'autoroutes-fr':'Autoroutes.fr — Francja',
  })[key] || key || 'Do konfiguracji';
}

function totalsByCurrency(route) {
  const totals = new Map();
  for (const section of route.sections || []) {
    const result = route.results?.[section.id];
    if (!result) continue;
    const calculatorKey = String(result?.calculatorKey || section?.calculatorKey || '');
    const value = reportNetValue(calculatorKey, result?.costValue);
    const currency = String(result?.currency || '').trim();
    if (value === null || !currency) continue;
    totals.set(currency, (totals.get(currency) || 0) + value);
  }
  return [...totals.entries()].map(([currency, total]) => ({ currency, total:roundMoney(total) }));
}

function formatSettings(settings) {
  if (!settings) return '';
  if (['M2', 'M3'].includes(String(settings.vehicleType || '').toUpperCase())) {
    const emission = ({
      EURO0:'EURO 0', EURO1:'EURO I', EURO2:'EURO II', EURO3:'EURO III',
      EURO4:'EURO IV', EURO5:'EURO V', EURO6:'EURO 6', LOW:'Niskoemisyjny', ZERO:'Bezemisyjny',
    })[String(settings.emissionClass || '').toUpperCase()] || settings.emissionClass || '';
    return [
      `Autobus ${String(settings.vehicleType).toUpperCase()}`,
      settings.category ? `kategoria ${settings.category}` : '',
      emission,
      settings.grossWeight ? `DMC ${settings.grossWeight} t` : '',
      settings.axleWeight ? `nacisk osi ${settings.axleWeight} t` : '',
      settings.height && settings.width && settings.length ? `${settings.length} × ${settings.width} × ${settings.height} m` : '',
    ].filter(Boolean).join('; ');
  }

  const categoryText = ({ '2':'2 osie', '3':'3 osie', '4':'4 osie', '5':'5 lub więcej osi' })[String(settings.vehicleCategory)] || '';
  const vehicle = ({
    '4':'Autobus 3501–7499 kg', '5':'Autobus 7500–11999 kg', '6':'Autobus 12000+ kg',
    '112':'Autobus 12 t lub więcej', '135':'Autobus 3.5 t – 12 t',
  })[String(settings.vehicleCategory)] || settings.vehicleCategory || '';
  const emission = ({
    '0':'EURO 0', '1':'EURO I', '2':'EURO II', '3':'EURO III', '4':'EURO IV',
    '5':'EURO V', '6':'EURO VI', '10':'EEV', '7':'EEV', '8':'ZEV',
  })[String(settings.emissionClass)] || settings.emissionClass || '';
  return [
    vehicle || categoryText,
    settings.baseAxles && settings.actualAxles ? `osie ${settings.baseAxles}/${settings.actualAxles}` : '',
    settings.axles ? `osie ${settings.axles}` : '',
    emission,
    settings.co2Class ? `CO₂ ${settings.co2Class}` : '',
    settings.date && settings.time ? `${settings.date} ${settings.time}` : '',
  ].filter(Boolean).join('; ');
}

function getSectionDistanceKm(section, result) {
  const resultDistance = finiteNumberOrNull(result?.distanceKm);
  if (resultDistance !== null) return round(resultDistance);
  const meters = finiteNumberOrNull(section?.distanceMeters);
  return meters !== null ? round(meters / 1000) : null;
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
}

function normalizeNumber(value) {
  const number = finiteNumberOrNull(value);
  return number === null ? '' : number;
}

function pickCalculatorScreenshot(result) {
  if (!result) return '';
  for (const value of [
    result.calculatorScreenshot,
    result.screenshotDataUrl,
    result.reportScreenshot,
    result.routeReportImage,
    result.screenshot_data_url,
  ]) {
    const normalized = normalizeImageDataUrl(value);
    if (normalized) return normalized;
  }
  return '';
}

function normalizeImageDataUrl(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^data:image\/(?:png|jpe?g|webp);base64,/i.test(text)) return text;
  if (/^iVBORw0KGgo/i.test(text)) return `data:image/png;base64,${text}`;
  if (/^\/9j\//.test(text)) return `data:image/jpeg;base64,${text}`;
  return '';
}


async function prepareRouteScreenshots(route) {
  if (!route || typeof route !== 'object') return route;

  const entries = Object.entries(route.results || {});
  if (!entries.length) return route;

  const results = { ...(route.results || {}) };
  let changed = false;

  for (const [sectionId, result] of entries) {
    const screenshot = pickCalculatorScreenshot(result);
    if (!/^data:image\/webp;base64,/i.test(screenshot)) continue;

    try {
      const converted = await convertWebpToPngDataUrl(screenshot);
      results[sectionId] = {
        ...result,
        calculatorScreenshot: converted,
        screenshotDataUrl: converted,
        reportScreenshot: converted,
      };
      changed = true;
    } catch (error) {
      console.warn('[D24 REPORT] Nie udało się przekonwertować WebP do PNG:', error);
    }
  }

  return changed ? { ...route, results } : route;
}

async function convertWebpToPngDataUrl(dataUrl) {
  const response = await fetch(dataUrl);
  if (!response.ok) throw new Error(`Nie udało się odczytać obrazu WebP: HTTP ${response.status}`);
  const blob = await response.blob();

  let source = null;
  let width = 0;
  let height = 0;

  if (typeof createImageBitmap === 'function') {
    source = await createImageBitmap(blob);
    width = source.width;
    height = source.height;
  } else {
    source = await loadImageElement(dataUrl);
    width = source.naturalWidth || source.width;
    height = source.naturalHeight || source.height;
  }

  if (!(width > 0 && height > 0)) {
    source?.close?.();
    throw new Error('Obraz WebP nie ma prawidłowych wymiarów.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha:false });
  if (!context) {
    source?.close?.();
    throw new Error('Przeglądarka nie udostępniła kontekstu Canvas 2D.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(source, 0, 0);
  source?.close?.();

  return canvas.toDataURL('image/png');
}

function loadImageElement(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Przeglądarka nie odczytała obrazu WebP.'));
    image.src = dataUrl;
  });
}

function addImage(workbook, sheet, dataUrl, tl, br) {
  const match = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,(.+)$/i);
  if (!match) return false;
  const sourceExtension = match[1].toLowerCase();
  if (sourceExtension === 'webp') return false;
  const extension = sourceExtension === 'png' ? 'png' : 'jpeg';
  const id = workbook.addImage({ base64:dataUrl, extension });
  sheet.addImage(id, { tl:cellToAnchor(tl), br:cellToAnchor(br), editAs:'oneCell' });
  return true;
}

function addImagePreservingOriginalSize(workbook, sheet, dataUrl, topLeftCell, options = {}) {
  const match = String(dataUrl || '').match(/^data:image\/(png|jpeg|jpg|webp);base64,(.+)$/i);
  if (!match) return null;

  const sourceExtension = match[1].toLowerCase();
  if (sourceExtension === 'webp') return null;

  const extension = sourceExtension === 'png' ? 'png' : 'jpeg';
  const dimensions = imagePixelDimensions(dataUrl) || { width:800, height:500 };
  const maxWidth = Math.max(200, Number(options.maxWidth) || 900);
  const maxHeight = Math.max(150, Number(options.maxHeight) || 820);

  // Nigdy nie powiększamy zrzutu. Duży obraz tylko proporcjonalnie zmniejszamy,
  // aby zmieścił się na stronie. Nie jest już rozciągany do A:L.
  const scale = Math.min(
    1,
    maxWidth / Math.max(1, dimensions.width),
    maxHeight / Math.max(1, dimensions.height)
  );
  const width = Math.max(1, Math.round(dimensions.width * scale));
  const height = Math.max(1, Math.round(dimensions.height * scale));

  const id = workbook.addImage({ base64:dataUrl, extension });
  sheet.addImage(id, {
    tl:cellToAnchor(topLeftCell),
    ext:{ width, height },
    editAs:'oneCell',
  });

  return {
    width,
    height,
    originalWidth:dimensions.width,
    originalHeight:dimensions.height,
    rowsUsed:Math.ceil(height / 24) + 1,
  };
}

function imagePixelDimensions(dataUrl) {
  try {
    const match = String(dataUrl || '').match(/^data:image\/(png|jpeg|jpg);base64,(.+)$/i);
    if (!match) return null;
    const binary = atob(match[2]);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);

    const type = match[1].toLowerCase();
    if (type === 'png') {
      if (bytes.length < 24) return null;
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const width = view.getUint32(16, false);
      const height = view.getUint32(20, false);
      return width > 0 && height > 0 ? { width, height } : null;
    }

    // JPEG: szukamy markera SOF zawierającego wymiary.
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xFF) {
        offset += 1;
        continue;
      }
      const marker = bytes[offset + 1];
      if ([0xC0,0xC1,0xC2,0xC3,0xC5,0xC6,0xC7,0xC9,0xCA,0xCB,0xCD,0xCE,0xCF].includes(marker)) {
        const height = (bytes[offset + 5] << 8) + bytes[offset + 6];
        const width = (bytes[offset + 7] << 8) + bytes[offset + 8];
        return width > 0 && height > 0 ? { width, height } : null;
      }
      if (marker === 0xD8 || marker === 0xD9) {
        offset += 2;
        continue;
      }
      const length = (bytes[offset + 2] << 8) + bytes[offset + 3];
      if (!length || length < 2) break;
      offset += 2 + length;
    }
  } catch (error) {
    console.warn('[D24 REPORT] Nie udało się odczytać wymiarów zrzutu:', error);
  }
  return null;
}

function cellToAnchor(cell) {
  const match = cell.match(/([A-Z]+)(\d+)/);
  let col = 0;
  for (const ch of match[1]) col = col * 26 + ch.charCodeAt(0) - 64;
  return { col:col - 1, row:Number(match[2]) - 1 };
}

async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 5000);
}

function parseExcelDate(value) {
  const [year, month, day] = String(value || '').split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day, 12, 0, 0) : null;
}

function weekdayLabel(value) {
  const date = parseExcelDate(value);
  return date ? date.toLocaleDateString('pl-PL', { weekday:'long' }) : '';
}

function directionTypeLabel(value) {
  const normalized = normalizeDirectionType(value);
  return normalized === 'both'
    ? 'Wyjazd + powrót — wspólne dane'
    : normalized === 'return'
      ? 'Powrót'
      : normalized === 'outbound'
        ? 'Wyjazd'
        : 'Nieokreślony';
}

function normalizeDirectionType(value) {
  const text = String(value || '').trim().toLowerCase();
  if (['both', 'wyjazd + powrót', 'wyjazd+powrót', 'wspólne'].includes(text)) return 'both';
  if (['return', 'powrot', 'powrót'].includes(text)) return 'return';
  if (['outbound', 'wyjazd'].includes(text)) return 'outbound';
  return '';
}

function directionOrder(value) {
  const normalized = normalizeDirectionType(value);
  return normalized === 'outbound' ? 0 : normalized === 'return' ? 1 : 2;
}

function formatDuration(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  return `${hours} h ${minutes} min`;
}

function round(number) {
  return Math.round(number * 10) / 10;
}

function roundMoney(number) {
  return Math.round((Number(number) + Number.EPSILON) * 100) / 100;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pl-PL', { minimumFractionDigits:2, maximumFractionDigits:2 });
}

function unique(values) {
  return [...new Set(values)];
}

function sanitize(value) {
  return String(value).replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').trim();
}
